import { type Camera } from './camera';
import { type Mesh } from './mesh';
import { LIT_FLAT_FS, LIT_FLAT_VS, SHADER_DEFINES } from './shaders';
import {
  BONE_UBO_BINDING,
  createBoneUbo,
  disposeBoneUbo,
  uploadBonePalette,
  type BoneUbo,
} from './webgl2/bone-ubo';
import { createGLContext, type GLContext } from './webgl2/context';
import {
  createInstanceBatch,
  disposeInstanceBatch,
  type InstanceBatch,
} from './webgl2/instance-batch';
import { disposeGpuMesh, uploadMesh, type GpuMesh } from './webgl2/mesh-gpu';
import { type Program, ProgramCache } from './webgl2/program';
import { disposeGpuTexture, uploadAtlasTexture, type GpuTexture } from './webgl2/texture';
import { type Mat4 } from '@/math/mat4';
import { type Vec3 } from '@/math/vec3';

const COMMON_UNIFORMS = [
  'u_view',
  'u_proj',
  'u_atlas',
  'u_lightDir',
  'u_lightColor',
  'u_skyColor',
  'u_groundColor',
] as const;

const NON_INSTANCED_UNIFORMS = [...COMMON_UNIFORMS, 'u_model', 'u_uvRect', 'u_tint'] as const;

const BONE_UBO_BINDINGS = [{ name: 'BoneUBO', binding: BONE_UBO_BINDING }] as const;

export interface DrawItem {
  mesh: GpuMesh;
  model: Mat4;
  uvRect: Float32Array;
  tint: Vec3;
}

export interface SkinnedItem {
  mesh: GpuMesh;
  model: Mat4;
  uvRect: Float32Array;
  tint: Vec3;
  palette: Float32Array;
}

export interface SunLight {
  direction: Vec3;
  color: Vec3;
  skyColor: Vec3;
  groundColor: Vec3;
}

export interface RenderStats {
  drawCalls: number;
  triangles: number;
  instances: number;
}

export class Renderer {
  private readonly ctx: GLContext;
  private readonly programs: ProgramCache;
  private readonly litFlat: Program;
  private readonly litFlatInstanced: Program;
  private readonly litFlatSkinned: Program;
  private readonly boneUbo: BoneUbo;
  readonly stats: RenderStats = { drawCalls: 0, triangles: 0, instances: 0 };

  constructor(canvas: HTMLCanvasElement) {
    this.ctx = createGLContext(canvas);
    this.programs = new ProgramCache(this.ctx.gl);
    this.programs.registerDefine(SHADER_DEFINES.INSTANCED, 'INSTANCED');
    this.programs.registerDefine(SHADER_DEFINES.SKINNED, 'SKINNED');
    this.litFlat = this.programs.acquire(
      { id: 'lit-flat', defines: 0 },
      LIT_FLAT_VS,
      LIT_FLAT_FS,
      NON_INSTANCED_UNIFORMS,
      [],
    );
    this.litFlatInstanced = this.programs.acquire(
      { id: 'lit-flat', defines: SHADER_DEFINES.INSTANCED },
      LIT_FLAT_VS,
      LIT_FLAT_FS,
      COMMON_UNIFORMS,
      [],
    );
    this.litFlatSkinned = this.programs.acquire(
      { id: 'lit-flat', defines: SHADER_DEFINES.SKINNED },
      LIT_FLAT_VS,
      LIT_FLAT_FS,
      NON_INSTANCED_UNIFORMS,
      [],
      BONE_UBO_BINDINGS,
    );
    this.boneUbo = createBoneUbo(this.ctx.gl);
  }

  get gl(): WebGL2RenderingContext {
    return this.ctx.gl;
  }

  uploadMesh(mesh: Mesh): GpuMesh {
    return uploadMesh(this.ctx.gl, mesh);
  }

  disposeMesh(m: GpuMesh): void {
    disposeGpuMesh(this.ctx.gl, m);
  }

  createInstanceBatch(mesh: GpuMesh, capacity: number): InstanceBatch {
    return createInstanceBatch(this.ctx.gl, mesh, capacity);
  }

  disposeInstanceBatch(batch: InstanceBatch): void {
    disposeInstanceBatch(this.ctx.gl, batch);
  }

  uploadAtlas(buffer: ArrayBuffer, size: number): GpuTexture {
    return uploadAtlasTexture(this.ctx.gl, buffer, size);
  }

  disposeAtlas(t: GpuTexture): void {
    disposeGpuTexture(this.ctx.gl, t);
  }

  resize(width: number, height: number): void {
    this.ctx.gl.viewport(0, 0, width, height);
  }

  render(
    camera: Camera,
    sun: SunLight,
    atlas: GpuTexture,
    items: readonly DrawItem[],
    batches: readonly InstanceBatch[],
    skinnedItems: readonly SkinnedItem[] = [],
  ): void {
    const gl = this.ctx.gl;
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, atlas.texture);

    this.stats.drawCalls = 0;
    this.stats.triangles = 0;
    this.stats.instances = 0;

    if (items.length > 0) {
      const p = this.litFlat;
      gl.useProgram(p.program);
      this.setCommonUniforms(p, camera, sun);
      let boundVao: WebGLVertexArrayObject | null = null;
      for (let i = 0; i < items.length; i++) {
        const item = items[i]!;
        gl.uniformMatrix4fv(p.u('u_model'), false, item.model);
        gl.uniform4fv(p.u('u_uvRect'), item.uvRect);
        gl.uniform3fv(p.u('u_tint'), item.tint);
        if (item.mesh.vao !== boundVao) {
          gl.bindVertexArray(item.mesh.vao);
          boundVao = item.mesh.vao;
        }
        gl.drawElements(gl.TRIANGLES, item.mesh.indexCount, item.mesh.indexType, 0);
        this.stats.drawCalls++;
        this.stats.triangles += item.mesh.indexCount / 3;
        this.stats.instances++;
      }
    }

    if (batches.length > 0) {
      const p = this.litFlatInstanced;
      gl.useProgram(p.program);
      this.setCommonUniforms(p, camera, sun);
      for (let i = 0; i < batches.length; i++) {
        const b = batches[i]!;
        if (b.count === 0) continue;
        gl.bindVertexArray(b.vao);
        gl.drawElementsInstanced(gl.TRIANGLES, b.indexCount, b.indexType, 0, b.count);
        this.stats.drawCalls++;
        this.stats.triangles += (b.indexCount / 3) * b.count;
        this.stats.instances += b.count;
      }
    }

    if (skinnedItems.length > 0) {
      const p = this.litFlatSkinned;
      gl.useProgram(p.program);
      this.setCommonUniforms(p, camera, sun);
      let boundVao: WebGLVertexArrayObject | null = null;
      for (let i = 0; i < skinnedItems.length; i++) {
        const item = skinnedItems[i]!;
        uploadBonePalette(gl, this.boneUbo, item.palette);
        gl.uniformMatrix4fv(p.u('u_model'), false, item.model);
        gl.uniform4fv(p.u('u_uvRect'), item.uvRect);
        gl.uniform3fv(p.u('u_tint'), item.tint);
        if (item.mesh.vao !== boundVao) {
          gl.bindVertexArray(item.mesh.vao);
          boundVao = item.mesh.vao;
        }
        gl.drawElements(gl.TRIANGLES, item.mesh.indexCount, item.mesh.indexType, 0);
        this.stats.drawCalls++;
        this.stats.triangles += item.mesh.indexCount / 3;
        this.stats.instances++;
      }
    }

    gl.bindVertexArray(null);
  }

  private setCommonUniforms(p: Program, camera: Camera, sun: SunLight): void {
    const gl = this.ctx.gl;
    gl.uniform1i(p.u('u_atlas'), 0);
    gl.uniformMatrix4fv(p.u('u_view'), false, camera.view);
    gl.uniformMatrix4fv(p.u('u_proj'), false, camera.proj);
    gl.uniform3fv(p.u('u_lightDir'), sun.direction);
    gl.uniform3fv(p.u('u_lightColor'), sun.color);
    gl.uniform3fv(p.u('u_skyColor'), sun.skyColor);
    gl.uniform3fv(p.u('u_groundColor'), sun.groundColor);
  }

  dispose(): void {
    disposeBoneUbo(this.ctx.gl, this.boneUbo);
    this.programs.dispose();
  }
}
